import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Compass, Loader2, RefreshCw } from "lucide-react";

export const HuntGuidancePanel = ({ userId, guidance, strongFound, onApplied, onRerun }) => {
  const [picked, setPicked] = useState(new Set());
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);
  if (!guidance) return null;

  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const apply = async () => {
    const suggestions = (guidance.suggestions || []).filter((s) => picked.has(s.id));
    if (!suggestions.length && !freeText.trim()) return toast.error("Pick a suggestion or tell me what to change");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/prospecting/targetspec/${userId}/apply`, { suggestions, freeText: freeText.trim() || null });
      setApplied(r.data.applied || []);
      toast.success("Targeting updated — run Hunt again to use it");
      onApplied?.(r.data.spec);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update targeting");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="hunt-guidance-panel">
      <div className="flex items-start gap-2">
        <Compass className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white" data-testid="hunt-guidance-question">{guidance.question}</p>
          {guidance.hint && <p className="text-xs text-gray-400 mt-1" data-testid="hunt-guidance-hint">{guidance.hint}</p>}
          <p className="text-[11px] text-amber-300/80 mt-1">
            Only {strongFound || 0} prospect{strongFound === 1 ? "" : "s"} passed the strict ICP gate — pick how to adjust, or type your own direction.
          </p>
        </div>
      </div>
      {applied ? (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="hunt-guidance-applied">
          <span className="text-xs text-ism-teal flex items-center gap-1"><Check className="h-3 w-3" />{applied.join(" · ") || "Targeting updated"}</span>
          <Button size="sm" onClick={onRerun} className="bg-ism-teal hover:bg-ism-teal/90 text-white h-7 text-xs" data-testid="hunt-guidance-rerun-btn">
            <RefreshCw className="h-3 w-3 mr-1" />Run Hunt again
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(guidance.suggestions || []).map((s) => (
              <button key={s.id} type="button" onClick={() => toggle(s.id)} title={s.reason || ""}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${picked.has(s.id) ? "border-ism-teal bg-ism-teal/20 text-ism-teal" : "border-white/15 bg-white/5 text-gray-300 hover:border-ism-teal/50"}`}
                data-testid={`hunt-guidance-suggestion-${s.id}`}>
                {picked.has(s.id) && <Check className="h-3 w-3 inline mr-1" />}{s.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input value={freeText} onChange={(e) => setFreeText(e.target.value)} placeholder="Or tell me in your own words, e.g. 'include mid-market banks, skip insurers'"
              className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="hunt-guidance-freetext" />
            <Button size="sm" onClick={apply} disabled={busy} className="bg-amber-500/80 hover:bg-amber-500 text-black h-8 text-xs" data-testid="hunt-guidance-apply-btn">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
